// ─────────────────────────────────────────────────────────────────────────────
// Locale-aware formatting: dates, times, numbers, money, relative time.
//
// Everything here is driven by the locale descriptor's `intl` tag, which
// carries the calendar and numbering system as Unicode extensions
// (`fa-IR-u-ca-persian-nu-arabext`). That is what makes "add a language" a
// data change: no formatter in this file branches on a locale id, and the
// Jalali↔Gregorian switch is a consequence of the tag, not an `if`.
//
// Where ICU's own data for a locale disagrees with how an Iranian clinic writes
// a date — or is simply wrong — the correction is declared on the DESCRIPTOR
// (`dateStyles`, `stripEra`, `numericDateOrder`, `monthNames`) and applied
// generically below. That is the escape hatch that keeps the rule above true:
// the knowledge that Arabic on the Persian calendar needs an era stripped and
// six of CLDR's twelve month names replaced lives with the locale, not in here.
//
// Two rules that are not negotiable:
//
//   • MONEY. Every service, gRPC message and GraphQL field in Parsina carries
//     an integer number of MINOR UNITS of the document's currency — WHOLE RIALS
//     for IRR, which is also what a blank currency means. Humans read TOMAN for
//     IRR: `formatMoney` takes rials and renders toman, exactly like
//     @parsina/framework's `fmtToman` — it only adds the locale. Passing a toman
//     figure to it bills a tenth of what was meant. Another currency is passed
//     as `{ currency }` and read through the kit's `currency` table; two
//     currencies are never added or compared. (A copy of the framework's
//     clinical `format.js` rule; screens reach it through `useTranslation()`,
//     whose `formatMoney` already defaults to the clinic's currency — money.js.)
//
//   • TIME ZONE. A clinic runs on Iran local time whatever language the UI is
//     in: an appointment at 14:30 is 14:30 in Tehran, not in the browser's
//     zone. Every date formatter pins `timeZone` from the descriptor.
//
// Instances are memoised per (locale, shape) because constructing an
// Intl.DateTimeFormat is expensive relative to using one, and these run inside
// list rows that re-render on every keystroke.
// ─────────────────────────────────────────────────────────────────────────────

import { calendarSystem } from '@parsina/framework/ui/lib/brand';
import { LOCALES, localeDescriptor } from './config';
import {
  DEFAULT_CURRENCY, currencyInfo, toDisplay, unitLabel as currencyUnitLabel,
} from '../lib/currency';

const memo = new Map();

function intl(Ctor, name, locale, options) {
  const key = `${name}|${locale}|${JSON.stringify(options)}`;
  let instance = memo.get(key);
  if (!instance) {
    instance = new Ctor(locale, options);
    memo.set(key, instance);
  }
  return instance;
}

/** Anything date-ish → Date, or null when it is not a usable instant. */
function toDate(value) {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// One instant inside each Jalali month, used only to ASK ICU for that month's
// name. The 15th at noon UTC: far enough from either boundary that no time zone
// — including the Asia/Tehran the formatters pin to — can push it into a
// neighbouring month. 1404 is an ordinary (non-leap) Jalali year, so every
// month has a 15th.
const MONTH_ANCHORS = [
  Date.UTC(2025, 3, 4),  Date.UTC(2025, 4, 5),  Date.UTC(2025, 5, 5),
  Date.UTC(2025, 6, 6),  Date.UTC(2025, 7, 6),  Date.UTC(2025, 8, 6),
  Date.UTC(2025, 9, 7),  Date.UTC(2025, 10, 6), Date.UTC(2025, 11, 6),
  Date.UTC(2026, 0, 5),  Date.UTC(2026, 1, 4),  Date.UTC(2026, 2, 6),
].map((ms) => ms + 12 * 3600 * 1000);

// The same for the GREGORIAN months, January first — the clinic's calendar under a
// brand on the Gregorian calendar (`calendar: 'gregory'`). The 15th at noon UTC, for
// the reason above.
const GREGORIAN_MONTH_ANCHORS = Array.from({ length: 12 }, (_, i) => Date.UTC(2025, i, 15, 12));

// One instant on each weekday, Saturday first — used only to ASK ICU for that
// day's name. 2025-08-02 is a Saturday; noon UTC keeps every one of them on the
// same calendar day in Asia/Tehran (UTC+3:30), which is the zone the formatters
// pin to.
const WEEKDAY_ANCHORS = Array.from(
  { length: 7 },
  (_, i) => Date.UTC(2025, 7, 2 + i) + 12 * 3600 * 1000,
);

// What an empty/unparseable value renders as. An em dash reads as "no value"
// in both scripts and never looks like a zero.
const EMPTY = '—';

// Named date shapes, so screens ask for a meaning rather than a field list and
// every «visit date» in the app looks the same.
//
//   short    ۱۴۰۴/۵/۱۲            8/3/25          dense tables
//   medium   ۱۲ مرداد ۱۴۰۴        Aug 3, 2025     the default
//   long     ۱۲ مرداد ۱۴۰۴        August 3, 2025  headers
//   full     یکشنبه، ۱۲ مرداد ۱۴۰۴ Sunday, …      day views
//   dayMonth ۱۲ مرداد            Aug 3           calendar chrome
//   weekdayDayMonth  یکشنبه، ۱۲ مرداد   Sunday, Aug 3   day navigator
//
// A descriptor may override individual shapes through its `dateStyles` field
// when ICU's pattern for that locale disagrees with how an Iranian clinic
// writes the date. The override is data, not a branch: a locale declares
// `numericDateOrder` and format.js applies it without knowing which locale it is.
const DATE_STYLES = {
  short:    { dateStyle: 'short' },
  medium:   { dateStyle: 'medium' },
  long:     { dateStyle: 'long' },
  dayMonth: { day: 'numeric', month: 'long' },
  monthYear: { month: 'long', year: 'numeric' },
  // The month alone — for a chart's month gridline, where the year is carried
  // by the one tick that crosses it rather than repeated on all twelve.
  month: { month: 'long' },
};

/**
 * Drop the era designator ICU appends when a locale is asked for a calendar
 * that is not its own default: Arabic on the Persian calendar renders
 * «١٢ مرداد ١٤٠٤ ه‍.ش», and `era: undefined` does not suppress it. The literal
 * that only ever joined the era to the date goes with it, so nothing is left
 * dangling at either end.
 */
function dropEra(parts) {
  if (!parts.some((part) => part.type === 'era')) return parts;
  return parts.filter((part, i) => {
    if (part.type === 'era') return false;
    if (part.type !== 'literal') return true;
    return parts[i - 1]?.type !== 'era' && parts[i + 1]?.type !== 'era';
  });
}

// Any Unicode decimal digit — Arabic-Indic ٠-٩ and Persian ۰-۹ included. Used
// to tell a numeric month («٥») from a month NAME («مرداد») without knowing
// which numbering system is in play.
const DIGITS_ONLY = /^\p{Nd}+$/u;
const DATE_FIELDS = ['year', 'month', 'day'];

/** A month rendered as a NAME rather than as digits. */
const isNamedMonth = (part) => part.type === 'month' && !DIGITS_ONLY.test(part.value);

/**
 * Substitute the descriptor's own month names for ICU's, for the locales where
 * CLDR's data for this language/calendar pairing is wrong. Only a month
 * rendered as a name is touched — a numeric month is already correct, and
 * replacing it would turn «٥» into a word.
 */
function withMonthNames(parts, names, monthNumber) {
  const replacement = names[monthNumber - 1];
  if (!replacement) return parts;
  return parts.map((part) => (isNamedMonth(part) ? { ...part, value: replacement } : part));
}

/**
 * Re-order an ALL-NUMERIC date into the descriptor's field order, keeping the
 * separator the locale itself chose. ICU writes the numeric Arabic date
 * day-first while Persian writes it year-first, and two RTL languages
 * disagreeing about the same calendar on the same screen is how a date gets
 * transcribed wrong.
 *
 * Returns null — meaning "leave this one alone" — for any shape that is not
 * exactly three numeric fields and their separators, so a month name, a
 * weekday or a time is never reordered.
 */
function reorderNumericDate(parts, order) {
  const fields = parts.filter((part) => DATE_FIELDS.includes(part.type));
  if (fields.length !== 3) return null;
  if (!fields.every((part) => DIGITS_ONLY.test(part.value))) return null;
  if (parts.some((part) => part.type !== 'literal' && !DATE_FIELDS.includes(part.type))) return null;

  const value = Object.fromEntries(fields.map((part) => [part.type, part.value]));
  const separator = parts.find((part) => part.type === 'literal')?.value ?? '/';
  return order.map((type) => value[type]).join(separator);
}

/**
 * All formatters for one locale. Built once per locale by the provider and
 * handed to screens through `useTranslation()`; nothing constructs these
 * ad hoc, so a rounding or calendar decision is made in exactly one place.
 */
export function createFormatters(localeId, { timeZone: clinicZone } = {}) {
  const {
    intl: tag, timeZone: localeZone, weekdaySeparator = ', ',
    dateStyles: styleOverrides, stripEra, numericDateOrder, monthNames,
    clinicCalendarIntl,
  } = localeDescriptor(localeId);

  // The CLINIC's zone when one is known (PM-0088) — <CurrencyProvider timeZone>
  // under the clinic context passes it; before a clinic is known, the
  // descriptor's default.
  const timeZone = clinicZone || localeZone;

  // Surfaces that are Jalali in every language fall back to the locale's own
  // tag, which is already Jalali for `fa`.
  const clinicTag = clinicCalendarIntl || tag;

  const dateTime = (options) => intl(Intl.DateTimeFormat, 'dt', tag, { timeZone, ...options });
  const number = (options) => intl(Intl.NumberFormat, 'n', tag, options);

  /** The options for a named shape, with the locale's override applied. */
  const styleOptions = (style) => {
    const name = (DATE_STYLES[style] || styleOverrides?.[style]) ? style : 'medium';
    return styleOverrides?.[name] || DATE_STYLES[name];
  };

  /**
   * The month's number (1-12) in this locale's own calendar. Forced to Latin
   * digits so it is an index rather than a glyph run — the tag's `-nu-` would
   * otherwise hand back «٥».
   */
  const monthNumber = (date) => Number(
    intl(Intl.DateTimeFormat, 'dt', tag, { timeZone, month: 'numeric', numberingSystem: 'latn' })
      .format(date),
  );

  /**
   * One date shape, with the descriptor's corrections applied. A locale that
   * declares none of them goes straight through ICU untouched, which is every
   * locale but `ar`.
   */
  const renderDate = (date, style) => {
    const formatter = dateTime(styleOptions(style));
    if (!stripEra && !numericDateOrder && !monthNames) return formatter.format(date);

    let parts = formatter.formatToParts(date);
    if (stripEra) parts = dropEra(parts);
    if (monthNames && parts.some(isNamedMonth)) {
      parts = withMonthNames(parts, monthNames, monthNumber(date));
    }
    if (numericDateOrder) {
      const reordered = reorderNumericDate(parts, numericDateOrder);
      if (reordered !== null) return reordered;
    }
    return parts.map((part) => part.value).join('').trim();
  };

  /**
   * The month of the CLINIC's calendar — Jalali — in the reading locale's
   * language and digits, whatever calendar that locale otherwise uses.
   *
   * The analysis timeline grids its axis on Jalali month boundaries, because
   * «this month» to an Iranian clinic means the Jalali month on the wall. A
   * gridline standing at 1 Mordad must therefore be labelled «مرداد» /
   * «Mordad», not «August» — the Gregorian month that line does not start.
   *
   * Composed from parts rather than asked for as a pattern, for two reasons ICU
   * forces: every locale puts the year on a different side of the month (Persian
   * emits «۱۴۰۴ مرداد», English «Mordad 1404»), and a non-default calendar drags
   * an era along with it («1404 AP», «١٤٠٤ ه‍.ش»). An axis label has to be the
   * same shape in all three languages and has no room for an era.
   */
  const formatClinicMonth = (value, { withYear = false } = {}) => {
    const date = toDate(value);
    if (!date) return EMPTY;
    const options = { month: 'long', ...(withYear ? { year: 'numeric' } : {}) };
    let parts = intl(Intl.DateTimeFormat, 'dt', clinicTag, { timeZone, ...options })
      .formatToParts(date);
    parts = dropEra(parts);
    if (monthNames && parts.some(isNamedMonth)) {
      parts = withMonthNames(parts, monthNames, monthNumber(date));
    }
    const month = parts.find((part) => part.type === 'month')?.value ?? '';
    const year = parts.find((part) => part.type === 'year')?.value ?? '';
    return withYear && year ? `${month} ${year}` : month;
  };

  /**
   * The twelve JALALI month names, index 0 = فروردین, in the reader's language.
   *
   * For a Jalali date PICKER — a month dropdown beside a year and a day. The
   * picker is Jalali in every language (`@parsina/framework`'s is Jalali-only,
   * and a clinic's day sheet, accounting period and shift report are all Jalali
   * whatever the panel is set to), so its month list is the clinic's calendar
   * rendered in the reader's words: «مرداد» / «Mordad», never «August».
   *
   * This replaced a hardcoded `J_MONTHS` array that every picker imported, and
   * which therefore showed Persian month names inside an otherwise-English
   * form. Built from a real date in each month rather than from a table so it
   * follows the same ICU data — and the same `monthNames` override — as every
   * other date on the screen.
   */
  //
  // A brand on the Gregorian calendar keeps Gregorian books, so there these are
  // the Gregorian months, index 0 = January (`formatClinicMonth` already reads the
  // Gregorian calendar through the descriptor's `calendars.gregory`).
  const clinicMonthNames = () => (calendarSystem('jalali') === 'gregory' ? GREGORIAN_MONTH_ANCHORS : MONTH_ANCHORS)
    .map((ms) => formatClinicMonth(ms));

  /**
   * The seven weekday names, index 0 = SATURDAY, in the reader's language.
   *
   * Saturday-first because that is the Iranian week: a clinic's working-days
   * selector, its weekly rota and its shift board all start on شنبه, and reordering
   * them for an English reader would move every doctor's Tuesday.
   *
   * `width` is passed through to `formatWeekday`, where 'short' is a trap: it
   * is NOT short in Persian — «یکشنبه» is the full word — and only English
   * abbreviates. A one-letter chip needs 'narrow'.
   *
   * This replaced hardcoded `['شنبه', 'یکشنبه', …]` arrays copied into four
   * screens, which rendered Persian weekdays in the English UI.
   */
  const weekdayNames = (width = 'long') => WEEKDAY_ANCHORS.map((ms) => formatWeekday(ms, width));

  /**
   * A date in one of the named shapes above.
   *
   * `full` is composed rather than delegated to `dateStyle: 'full'`. Node's
   * bundled ICU emits the Persian full pattern as year–month–day–weekday
   * («۱۴۰۴ مرداد ۱۲، یکشنبه»), which is both wrong for Persian and the reverse
   * of what the same ICU gives for `long`. Browsers disagree with it and with
   * each other. Composing weekday + long date makes the one date shape a
   * doctor reads at the top of a day sheet identical everywhere, which matters
   * more here than deferring to CLDR.
   */
  const formatDate = (value, style = 'medium') => {
    const date = toDate(value);
    if (!date) return EMPTY;
    // `full` and `weekdayDayMonth` are the two composed shapes.
    if (style === 'full' || style === 'weekdayDayMonth') {
      const weekday = dateTime({ weekday: 'long' }).format(date);
      const rest = renderDate(date, style === 'full' ? 'long' : 'dayMonth');
      return `${weekday}${weekdaySeparator}${rest}`;
    }
    return renderDate(date, style);
  };

  /**
   * Clock time, 24-hour in every locale. Iranian clinical scheduling is written
   * and spoken in 24-hour time, and an English UI for an Iranian clinic that
   * suddenly says "2:30 PM" while the printed day sheet says 14:30 is a
   * transcription error waiting to happen.
   */
  const formatTime = (value) => {
    const date = toDate(value);
    if (!date) return EMPTY;
    return dateTime({ hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
  };

  const formatDateTime = (value, style = 'medium') => {
    const date = toDate(value);
    if (!date) return EMPTY;
    return `${formatDate(date, style)} ${formatTime(date)}`;
  };

  /**
   * Weekday name. The three widths are NOT what the names suggest in Persian,
   * so pick by looking at the output rather than by analogy with English:
   *
   *   long    یکشنبه   Sunday   الأحد
   *   short   یکشنبه   Sun      الأحد     ← Persian/Arabic `short` is the FULL word
   *   narrow  ی        S        ح
   *
   * A one-letter chip — the reception week strip — needs `narrow`; anything that
   * reads as a word needs `long`. `short` only differs from `long` in English.
   */
  const formatWeekday = (value, width = 'long') => {
    const date = toDate(value);
    if (!date) return EMPTY;
    return dateTime({ weekday: width }).format(date);
  };

  /**
   * A plain count — NOT money, so no toman conversion. Persian digits in `fa`,
   * Latin in `en`, grouped in both.
   */
  const formatNumber = (value, options) => {
    if (value == null || value === '' || !Number.isFinite(Number(value))) return EMPTY;
    return number(options).format(Number(value));
  };

  const formatPercent = (value, options) => {
    if (value == null || !Number.isFinite(Number(value))) return EMPTY;
    return number({ style: 'percent', maximumFractionDigits: 0, ...options }).format(Number(value) / 100);
  };

  /**
   * Clinic money: MINOR UNITS of `currency` in → the figure a human reads out.
   *
   * No currency — or '' or 'IRR' — is the contract this always had: WHOLE RIALS
   * in, a toman figure out, and `withUnit` appends the caller's `unitLabel` (the
   * app's own «تومان»/«Toman» catalogue word), or nothing when there is none.
   *
   * Any other currency is divided by its own divisor and shown with its own
   * fraction digits (USD 2, KWD 3) in this locale's digits. With `withUnit` its
   * unit is the currency table's word — `unitStyle` 'long' («US dollar»,
   * default) or 'short' («USD») — and a caller's `unitLabel` is IGNORED: at every
   * call site written before currencies existed it is a toman word, and a
   * non-IRR figure must never read «تومان».
   *
   * A code outside the table (a server ahead of this build) is NOT guessed at and
   * NOT folded to IRR: it renders its RAW integer minor units, ungrouped, and — with
   * `withUnit` — its code: «12345 XYZ» (lib/currency.js). The framework's copy of
   * this function throws there instead; a reception desk must not go blank.
   *
   * Otherwise the same function as @parsina/framework's clinical `formatMoney`
   * (v0.21.0); this app keeps its own copy of the formatters.
   */
  const formatMoney = (amountMinor, {
    currency, withUnit = false, unitLabel = '', unitStyle = 'long',
  } = {}) => {
    const info = currencyInfo(currency);
    if (info.code !== DEFAULT_CURRENCY) {
      const value = toDisplay(amountMinor, info.code);
      if (value == null) return EMPTY;
      const text = number(info.unknown
        ? { useGrouping: false, maximumFractionDigits: 0 }
        : { minimumFractionDigits: info.minDisplayDecimals, maximumFractionDigits: info.maxDisplayDecimals },
      ).format(value);
      return withUnit ? `${text} ${currencyUnitLabel(info.code, localeId, unitStyle)}` : text;
    }

    if (amountMinor == null || amountMinor === '' || !Number.isFinite(Number(amountMinor))) return EMPTY;
    const toman = Number(amountMinor) / 10;   // IRR: whole rials → toman
    // Prices are entered in toman so sub-toman precision cannot exist, but a
    // percentage discount can land on a half-toman: show it rather than
    // silently rounding money away.
    const text = number({ maximumFractionDigits: 1 }).format(toman);
    return withUnit && unitLabel ? `${text} ${unitLabel}` : text;
  };

  /**
   * «۳ روز پیش» / «3 days ago». Picks the largest unit that fits, so a two-hour
   * gap reads in hours and a two-month gap in months.
   */
  const formatRelativeTime = (value, now = Date.now()) => {
    const date = toDate(value);
    if (!date) return EMPTY;
    const rtf = intl(Intl.RelativeTimeFormat, 'rt', tag, { numeric: 'auto' });
    const deltaSeconds = (date.getTime() - now) / 1000;
    const units = [
      ['year', 31536000], ['month', 2592000], ['week', 604800],
      ['day', 86400], ['hour', 3600], ['minute', 60],
    ];
    for (const [unit, seconds] of units) {
      if (Math.abs(deltaSeconds) >= seconds) {
        return rtf.format(Math.round(deltaSeconds / seconds), unit);
      }
    }
    return rtf.format(Math.round(deltaSeconds), 'second');
  };

  /** «الف، ب و ج» / «a, b, and c» — grammatically correct list joining. */
  const formatList = (items, type = 'conjunction') => {
    const list = (items || []).filter((item) => item != null && item !== '').map(String);
    if (!list.length) return '';
    if (typeof Intl.ListFormat !== 'function') return list.join(', ');
    return intl(Intl.ListFormat, 'lf', tag, { style: 'long', type }).format(list);
  };

  /**
   * Digits only, in the locale's numbering system, with NO grouping — for
   * things that are digit strings rather than quantities: a national id, a
   * medical-council code, a file number. Grouping one of those would read as a
   * different identifier.
   */
  // '0'…'9' in this locale's numbering system, resolved once.
  const digitMap = Array.from({ length: 10 }, (_, d) => number({ useGrouping: false }).format(d));

  const formatDigits = (value) => {
    if (value == null || value === '') return '';
    return String(value).replace(/\d/g, (d) => digitMap[Number(d)]);
  };

  return {
    timeZone,
    formatDate, formatTime, formatDateTime, formatWeekday, formatClinicMonth,
    clinicMonthNames, weekdayNames,
    formatNumber, formatPercent, formatMoney, formatRelativeTime,
    formatList, formatDigits,
  };
}

/**
 * Phone numbers, emails and URLs are never localised — they are LTR digit/ASCII
 * runs that must read left-to-right even inside an RTL paragraph. Spread this
 * onto the element that holds one; it is the direction equivalent of not
 * translating a proper noun.
 *
 *   <span {...LTR_TEXT}>{doctor.phone}</span>
 *
 * `unicode-bidi: isolate` is what stops a trailing «:» or a following Persian
 * word from being dragged to the wrong end of the number.
 */
/**
 * Any shipped locale's digits IN, ASCII out.
 *
 * The inverse of `formatDigits`, and a PARSER rather than a renderer: a
 * national id, a phone or a file number typed on a Persian or an Arabic
 * keyboard has to reach the server as ASCII whatever the panel is set to. It
 * deliberately does not follow the active locale — someone reading the panel in
 * English may still be typing on a Persian keyboard.
 *
 * Built from the REGISTRY, not from a hand-written list of digit ranges, so a
 * fourth language stays a data change: `Intl.NumberFormat` is asked for each
 * locale's 0–9 and the mapping is inverted. Enumerating ranges by hand is how
 * this came to fold Persian «۰۱۲» and not Arabic-Indic «٠١٢» — two different
 * blocks that look alike at a glance, and the exact trap the doctor app hit
 * when it added Arabic.
 */
const ASCII_BY_DIGIT = (() => {
  const map = new Map();
  for (const descriptor of Object.values(LOCALES)) {
    let format;
    try {
      format = new Intl.NumberFormat(descriptor.intl, { useGrouping: false });
    } catch {
      continue;                       // an unsupported tag must not break input
    }
    for (let value = 0; value <= 9; value += 1) map.set(format.format(value), String(value));
  }
  return map;
})();

export function toAsciiDigits(value) {
  if (value == null || value === '') return '';
  return String(value).replace(/./gu, (ch) => ASCII_BY_DIGIT.get(ch) ?? ch);
}

export const LTR_TEXT = { dir: 'ltr', style: { unicodeBidi: 'isolate' } };

/**
 * Mirror an icon whose MEANING is directional — a "drill in" chevron, a
 * previous/next arrow, a progress caret. Spread onto the icon's style:
 *
 *   <ChevronLeft style={MIRROR_ICON} />
 *
 * `--dir-flip` is declared in styles.css: +1 under `[dir="rtl"]`, -1 under
 * `[dir="ltr"]`. Mirroring the glyph beats swapping ChevronLeft for
 * ChevronRight at each site, because the swap has to be remembered everywhere
 * and this cannot be half-applied.
 *
 * Do NOT use it on icons that merely happen to be asymmetric. A mirrored
 * magnifier, phone handset, logo or play triangle is the classic RTL bug —
 * which is exactly why this is opt-in per icon rather than a rule on <svg>.
 */
export const MIRROR_ICON = { transform: 'scaleX(var(--dir-flip, 1))' };
